import React, { useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const ProductionReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // -----------------------------
  // Helpers
  // -----------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && String(val).trim() !== ""
      ? val
      : fallback;
  };

  const toInt = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const toStr = (v, fallback = "") => {
    if (v === null || v === undefined) return fallback;
    const s = String(v).trim();
    return s !== "" ? s : fallback;
  };

  const formatDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
      if (value.includes("T")) return value.slice(0, 10);
      if (value.length >= 10) return value.slice(0, 10);
      return value;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  };

  const normalizeDateForInput = (value, fallback = "") => {
    if (!value) return fallback;

    // already yyyy-MM-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    // dd/MM/yyyy -> yyyy-MM-dd
    const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value).trim());
    if (dmy) {
      const [, dd, mm, yyyy] = dmy;
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toISOString().slice(0, 10);
  };

  const toDMY = (value) => {
    if (!value) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;

    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
    if (iso) {
      const [, yyyy, mm, dd] = iso;
      return `${dd}/${mm}/${yyyy}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString("en-IN") : "";
  };

  // -----------------------------
  // localStorage values
  // -----------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // -----------------------------
  // parsed values
  // -----------------------------
  const branchid = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // -----------------------------
  // common headers
  // -----------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": toStr(finYearId, ""),
      "x-finyear": toStr(finYear, ""),
      "x-branchid": String(branchid || ""),
      "x-branchtype": toStr(branchtype, "HO"),
      "x-userid": String(userid || ""),
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fromDate: normalizeDateForInput("01/01/2021"),
    toDate: normalizeDateForInput("30/12/2025"),
  });

  const [productionData, setProductionData] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // Search API Call
  // ---------------------------
  const handleSearch = async () => {
    if (!branchid) {
      toast.error("Branch ID missing in localStorage");
      return;
    }

    if (!userid) {
      toast.error("User ID missing in localStorage");
      return;
    }

    if (!formData.fromDate || !formData.toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/productionListing/search`, {
        params: {
          branchid,
          loginuserid: userid,
          fromDate: toDMY(formData.fromDate),
          toDate: toDMY(formData.toDate),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setProductionData(data);

      if (data.length === 0) toast.info("No Records found");
      else toast.success(`Found ${data.length} record(s)`);
    } catch (err) {
      console.error("Search error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to fetch production listing"
      );
      setProductionData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Print handler
  // ---------------------------
  const handlePrint = (row) => {
    const prodId =
      row.productionid ??
      row.productionId ??
      row.prodId ??
      row.ProductionId ??
      row.fld_ProductionId ??
      row.id;

    if (!prodId) {
      toast.error("Production ID missing in this row");
      return;
    }

    window.open(
      `/TarMixing/Tar_Production_Report_Print?prodId=${prodId}&branchid=${branchid}`,
      "_blank"
    );
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Report
            </h1>

            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="flex justify-center items-center mt-6 gap-4">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {productionData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <div className={`overflow-x-auto ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <table className="min-w-full text-sm">
                  <thead className={`${isDark ? "bg-gray-700 text-white" : "bg-blue-700 text-white"}`}>
                    <tr>
                      <th className="px-4 py-3 text-center">S.No</th>
                      <th className="px-4 py-3 text-center">Item Name</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Quantity</th>
                      <th className="px-4 py-3 text-center">Report</th>
                    </tr>
                  </thead>

                  <tbody>
                    {productionData.map((row, idx) => {
                      const itemName =
                        row.ItemName ??
                        row.itemName ??
                        row.fld_ItemName ??
                        row.ProductionName ??
                        row.productionName ??
                        row.fld_ProductionName ??
                        row.fld_productionname ??
                        "-";

                      const qty =
                        row.Quantity ??
                        row.quantity ??
                        row.fld_Quantity ??
                        row.Qty ??
                        row.qty ??
                        row.fld_productionqty ??
                        "";

                      const dateRaw =
                        row.fld_ProductionDate ??
                        row.productionDate ??
                        row.date ??
                        row.fld_Date ??
                        row.entryDate ??
                        row.fld_date ??
                        "";

                      const rowKey =
                        row.productionid ??
                        row.productionId ??
                        row.prodId ??
                        row.fld_ProductionId ??
                        row.id ??
                        idx;

                      return (
                        <tr
                          key={rowKey}
                          className={`border-t ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          } ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-900/30"
                              : "bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center">{idx + 1}</td>
                          <td className="px-4 py-3 text-center">{itemName}</td>
                          <td className="px-4 py-3 text-center">{formatDate(dateRaw)}</td>
                          <td className="px-4 py-3 text-center">{formatNumber(qty)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handlePrint(row)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionReport;